import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, LockSimple, LockSimpleOpen, FileXls, FilePdf, Trophy } from "@phosphor-icons/react";

const EDIT_COLS = [
  { key: "pts", label: "PTS" },
  { key: "tpm", label: "3PM" },
  { key: "tpa", label: "3PA" },
  { key: "ftm", label: "FTM" },
  { key: "fta", label: "FTA" },
  { key: "reb", label: "REB" },
  { key: "ast", label: "AST" },
  { key: "stl", label: "STL" },
  { key: "blk", label: "BLK" },
  { key: "pf", label: "PF" },
  { key: "tech", label: "T" },
];
const blank = () => ({ pts: 0, tpm: 0, tpa: 0, ftm: 0, fta: 0, fgm: 0, fga: 0, reb: 0, ast: 0, stl: 0, blk: 0, pf: 0, tech: 0 });

export function BoxScoreEditor({ game, teams, onClose, onSaved }) {
  const [rows, setRows] = useState({ home: [], away: [] });
  const [locked, setLocked] = useState(!!game.locked);
  const [week, setWeek] = useState(game.week != null ? String(game.week) : "");
  const [pog, setPog] = useState(game.player_of_game || "");
  const [homeScore, setHomeScore] = useState(game.home_score || 0);
  const [awayScore, setAwayScore] = useState(game.away_score || 0);
  const [saving, setSaving] = useState(false);

  const teamOf = (id) => teams.find((t) => t.id === id);

  const load = useCallback(async () => {
    const [hp, ap, box] = await Promise.all([
      api.get(`/players?team_id=${game.home_team_id}`),
      api.get(`/players?team_id=${game.away_team_id}`),
      api.get(`/games/${game.id}/boxscore`).catch(() => ({ data: { home_stats: [], away_stats: [] } })),
    ]);
    const byId = {};
    [...(box.data.home_stats || []), ...(box.data.away_stats || [])].forEach((s) => { byId[s.player_id] = s; });
    const mk = (players, side) => players.map((p) => ({
      player_id: p.id, team_id: p.team_id, side, name: p.name, number: p.number ?? null,
      ...blank(), ...(byId[p.id] || {}),
    }));
    setRows({ home: mk(hp.data, "home"), away: mk(ap.data, "away") });
  }, [game]);
  useEffect(() => { load(); }, [load]);

  const setCell = (side, pid, key, val) => {
    const n = Math.max(0, parseInt(val, 10) || 0);
    setRows((prev) => ({
      ...prev,
      [side]: prev[side].map((r) => (r.player_id === pid ? { ...r, [key]: n } : r)),
    }));
  };

  const toggleLock = async () => {
    try {
      await api.post(`/games/${game.id}/${locked ? "unlock" : "lock"}`);
      setLocked(!locked);
      toast.success(locked ? "Game unlocked — you can edit stats" : "Game locked");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    if (locked) { toast.error("Unlock the game before saving edits"); return; }
    const all = [...rows.home, ...rows.away];
    const stats = all
      .filter((r) => EDIT_COLS.some((c) => (r[c.key] || 0) > 0) || (r.fga || 0) > 0 || (r.fgm || 0) > 0)
      .map((r) => ({ ...r, number: r.number ?? null }));
    setSaving(true);
    try {
      await api.put(`/games/${game.id}/stats`, {
        home_score: parseInt(homeScore, 10) || 0,
        away_score: parseInt(awayScore, 10) || 0,
        week: week === "" ? null : parseInt(week, 10),
        stats,
        player_of_game: pog,
        home_fouls: game.home_fouls || 0, away_fouls: game.away_fouls || 0,
        home_timeouts: game.home_timeouts || 0, away_timeouts: game.away_timeouts || 0,
      });
      toast.success("Box score saved — player totals recalculated");
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const download = async (ext) => {
    try {
      const res = await api.get(`/games/${game.id}/export.${ext}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `boxscore-${teamOf(game.home_team_id)?.name || "home"}-vs-${teamOf(game.away_team_id)?.name || "away"}.${ext}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const hsNum = parseInt(homeScore, 10) || 0, asNum = parseInt(awayScore, 10) || 0;
  const winSide = hsNum > asNum ? "home" : (asNum > hsNum ? "away" : null);
  const eligiblePog = winSide ? rows[winSide] : [];

  const TeamGrid = ({ side, teamId }) => (
    <div className="mb-5">
      <div className="font-display text-lg uppercase mb-2">{teamOf(teamId)?.name || "TBD"}</div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-xs min-w-[640px]">
          <thead>
            <tr className="bg-[#1A1A1A] text-white/60 uppercase">
              <th className="text-left px-2 py-2 sticky left-0 bg-[#1A1A1A]">Player</th>
              {EDIT_COLS.map((c) => <th key={c.key} className="px-1 py-2 w-12 text-center">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows[side].map((r) => (
              <tr key={r.player_id} className="border-t border-white/5">
                <td className="px-2 py-1.5 sticky left-0 bg-[#121212] whitespace-nowrap uppercase">
                  <span className="text-[#FF5722] mr-1">{r.number != null ? `#${r.number}` : ""}</span>{r.name}
                </td>
                {EDIT_COLS.map((c) => (
                  <td key={c.key} className="px-1 py-1">
                    <input type="number" min="0" value={r[c.key]} disabled={locked}
                      data-testid={`edit-${side}-${r.player_id}-${c.key}`}
                      onChange={(e) => setCell(side, r.player_id, c.key, e.target.value)}
                      className="w-11 bg-[#1A1A1A] border border-white/15 px-1 py-1 text-center tabular-nums disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-[#FF5722]" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="bg-[#121212] border border-white/10 w-full max-w-4xl my-4" onClick={(e) => e.stopPropagation()} data-testid="boxscore-editor">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#121212] z-10">
          <h3 className="font-display text-2xl uppercase">Edit Box Score</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={22} /></button>
        </div>
        <div className="p-5">
          <div className="flex flex-wrap items-end gap-3 mb-5">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-white/60 mb-1">{teamOf(game.home_team_id)?.name} Score</label>
              <input type="number" min="0" value={homeScore} disabled={locked} onChange={(e) => setHomeScore(e.target.value)} data-testid="edit-home-score" className="w-24 bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm text-center disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-[#FF5722]" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-white/60 mb-1">{teamOf(game.away_team_id)?.name} Score</label>
              <input type="number" min="0" value={awayScore} disabled={locked} onChange={(e) => setAwayScore(e.target.value)} data-testid="edit-away-score" className="w-24 bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm text-center disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-[#FF5722]" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-white/60 mb-1">Week</label>
              <input type="number" min="1" value={week} disabled={locked} onChange={(e) => setWeek(e.target.value)} data-testid="edit-week" className="w-20 bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm text-center disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-[#FF5722]" />
            </div>
            <div className="min-w-[180px]">
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-white/60 mb-1"><Trophy size={11} weight="fill" className="inline text-[#FF5722]" /> Player of the Game <span className="text-white/40 normal-case font-normal">(winning team)</span></label>
              <select value={pog} disabled={locked} onChange={(e) => setPog(e.target.value)} data-testid="edit-pog" className="w-full bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-[#FF5722]">
                <option value="">Auto (top winner)</option>
                {eligiblePog.map((p) => <option key={p.player_id} value={p.player_id}>{`${p.number != null ? `#${p.number} ` : ""}${p.name}`}</option>)}
              </select>
            </div>
            <button onClick={toggleLock} data-testid="toggle-lock-btn" className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide transition-all duration-200 ${locked ? "bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25" : "bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/40 hover:bg-[#00E676]/25"}`}>
              {locked ? <><LockSimple size={15} weight="fill" /> Locked — Unlock to Edit</> : <><LockSimpleOpen size={15} /> Unlocked</>}
            </button>
          </div>

          {locked && <p className="text-[#FF3B30]/80 text-sm mb-4 border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2">This game's stats are locked. Click "Unlock to Edit" to make corrections.</p>}

          <TeamGrid side="home" teamId={game.home_team_id} />
          <TeamGrid side="away" teamId={game.away_team_id} />

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/10">
            <button onClick={() => download("xlsx")} data-testid="export-xlsx-btn" className="flex items-center gap-2 border border-white/15 hover:border-[#00E676] hover:text-[#00E676] text-white/70 text-sm font-bold uppercase tracking-wide px-4 py-2.5 transition-all duration-200">
              <FileXls size={18} /> Excel
            </button>
            <button onClick={() => download("pdf")} data-testid="export-pdf-btn" className="flex items-center gap-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-white/70 text-sm font-bold uppercase tracking-wide px-4 py-2.5 transition-all duration-200">
              <FilePdf size={18} /> PDF
            </button>
            <button onClick={save} disabled={saving || locked} data-testid="save-boxscore-btn" className="ml-auto bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-black font-bold uppercase tracking-wide px-6 py-2.5 transition-all duration-200">
              {saving ? "Saving..." : "Save Box Score"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
